import { enableDebugTools, BrowserModule, bootstrapApplication } from '@angular/platform-browser';

import { provideAppInitializer, inject, importProvidersFrom } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CsrfInterceptor } from './app/interceptors/csrf.interceptor';
import { AuthService } from './app/services/auth.service';
import { RolesService } from './app/services/roles.service';
import { FeaturesService } from './app/services/features.service';
import { firstValueFrom } from 'rxjs';
import { NgbDropdownModule, NgbNavModule, NgbAccordionModule, NgbTooltipModule, NgbToastModule, NgbCollapseModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app/app-routing.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxEditorModule } from 'ngx-editor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppComponent } from './app/app.component';

declare global {
  interface Array<T> {
    groupBy<K extends keyof T>(key: K): Record<string, T[]>;
  }
}

Array.prototype.groupBy = function <T, K extends keyof T>(
  key: K
): Record<string, T[]> {
  return this.reduce((result: Record<string, T[]>, item: T) => {
    const groupKey = String(item[key]);
    (result[groupKey] = result[groupKey] || []).push(item);
    return result;
  }, {});
};

async function initApp(authService: AuthService, rolesService: RolesService, featuresService: FeaturesService): Promise<void> {
  const isAuth = await firstValueFrom(authService.isAuthorized(true));
  if (isAuth) {
    await rolesService.initRoles();
  }
  await firstValueFrom(featuresService.refreshFeatures());
}

const debug = false;

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(BrowserModule, NgbDropdownModule, NgbNavModule, NgbAccordionModule, NgbTooltipModule, NgbToastModule, NgbCollapseModule, NgbPaginationModule, NgxSliderModule, FormsModule, ReactiveFormsModule, AppRoutingModule, DragDropModule, NgxEditorModule),
        { provide: HTTP_INTERCEPTORS, useClass: CsrfInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
        provideAppInitializer(async () => {
            const authService = inject(AuthService);
            const rolesService = inject(RolesService);
            const featuresService = inject(FeaturesService);
            return initApp(authService, rolesService, featuresService);
        }),
        provideAnimations(),
    ]
})
.then((appRef) => {
  if (debug) {
    const componentRef = appRef.components[0];
    enableDebugTools(componentRef);
  }
})
.catch(err => console.error(err));
